/**
 * field-errors (2026-11-01, plan #83, Mario's boxes 68–70) — a refused field
 * in the form's own words: "Phone: use the international format, starting
 * with + and the country code (for example +447700900123)".
 *
 * The SERVER decides what is named and writes the fix (`fields` on a refusal,
 * from `describeInputInvalidFields` and the one words table in
 * `@layer/canonical-actions/field-fix-words`); this module only puts the
 * form's label in front. So the words are the same on every form, in the MCP
 * connector, and in the /status form's own check.
 *
 * A field is named only where the form SHOWS a row for it (box 69, Q6): a
 * refused field the form fills in itself (an id, a hidden value) is the
 * page's bug, and the refusal's own message stands — today's line.
 *
 * Pure, with no Supabase import, so it is tested on its own (the dispatch
 * helper loads Supabase when imported).
 */

/** One field a refusal names, and the fix — never the value typed. */
export interface FailureField {
  /** The input's own name, as the action names it: `phone`, `patch.phone`, `entries[1].phone`. */
  readonly field: string;
  /** What to do, starting in lower case: "use a date written year-month-day …". */
  readonly fix: string;
}

/** An input name the server sends: dotted names, `[n]` for a list item. */
const FIELD_NAME = /^[A-Za-z_][A-Za-z0-9_]{0,63}(?:\[\d{1,4}\]|\.[A-Za-z_][A-Za-z0-9_]{0,63})*$/;
const MAX_FIX_CHARS = 300;

/** The failure body's `fields`, well-formed entries only, or `null` when it has none. */
export function failureFields(raw: unknown): ReadonlyArray<FailureField> | null {
  if (!Array.isArray(raw)) return null;
  const fields = raw.flatMap((entry: unknown): FailureField[] => {
    if (entry === null || typeof entry !== "object") return [];
    const { field, fix } = entry as { field?: unknown; fix?: unknown };
    return typeof field === "string" &&
      FIELD_NAME.test(field) &&
      typeof fix === "string" &&
      fix.length > 0 &&
      fix.length <= MAX_FIX_CHARS
      ? [{ field, fix }]
      : [];
  });
  return fields.length > 0 ? fields : null;
}

/**
 * A form label cut to its name: "Reason — the client's assistant can quote
 * this" → "Reason", "Partial amount (£) — blank = full" → "Partial amount",
 * "Note (optional)" → "Note", "Why is it closed?" → "Why is it closed".
 */
export function shortLabel(label: string): string {
  const name = (label.split(/\s[—–-]\s/u)[0] ?? "")
    .replace(/\s*\([^()]*\)\s*$/u, "")
    .replace(/[\s?:*.]+$/u, "")
    .trim();
  return name;
}

/**
 * A readable form of an input name, for a row with no label of its own:
 * "patch.phone" → "Phone", "starts_on" → "Starts on", "entries[1].phone" →
 * "Phone (row 2)". Never the raw path.
 */
export function readableFieldName(field: string): string {
  const names = field.split(/\.|\[\d+\]/u).filter((s) => s.length > 0);
  const last = names[names.length - 1] ?? field;
  const words = last.replace(/_+/gu, " ").trim();
  const name = words.length > 0 ? words.charAt(0).toUpperCase() + words.slice(1) : field;
  const index = /\[(\d+)\](?!.*\[\d+\])/u.exec(field)?.[1];
  return index === undefined ? name : `${name} (row ${Number(index) + 1})`;
}

/**
 * Where a form's rows are: the label each input name is shown under. A
 * function form answers for names it computes (a list's rows); `null` or
 * `undefined` means "no row here".
 */
export type FormRows =
  | Readonly<Record<string, string>>
  | ((field: string) => string | null | undefined);

function rowLabel(rows: FormRows, field: string): string | null {
  const label =
    typeof rows === "function"
      ? rows(field)
      : Object.prototype.hasOwnProperty.call(rows, field)
        ? rows[field]
        : undefined;
  return typeof label === "string" ? label : null;
}

/**
 * The line a form shows for a refusal: "<Label>: <fix>" for the first field
 * the refusal names that has a row in this form, else the refusal's own
 * message. A row whose label is empty is named by `readableFieldName`.
 */
export function failureText(
  failure: { readonly message: string; readonly fields?: ReadonlyArray<FailureField> | undefined },
  rows: FormRows,
): string {
  for (const { field, fix } of failure.fields ?? []) {
    const label = rowLabel(rows, field);
    if (label === null) continue;
    const name = shortLabel(label);
    return `${name.length > 0 ? name : readableFieldName(field)}: ${fix}`;
  }
  return failure.message;
}
